import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ProfileContainer,
  BackButton,
  ProfileCard,
  ProfileHeader,
  PlayerImage,
  PlayerInfoStyled,
  ProfileContent,
  InfoSection,
  InfoGrid,
  StatsTable,
  StatRow,
  StatHeader,
  StatCell,
  SponsorImage
} from './PlayerProfile.styles';
import { PlayerInfo } from '../../models/PlayerInfo';
import { PlayerStatsInterface } from '../../models/PlayerStats';
import config from '../../config.json';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import { getMainTeamWebsite } from '../../constants/mainTeams';

const formatNA = (value: string | undefined | null) => value || 'N/A';

const parseJsonSafely = (text: string): any => {
  const trimmed = text.trim();

  try {
    return JSON.parse(trimmed);
  } catch {
    // Extract the first JSON object/array using brace/bracket matching,
    // ignoring braces/brackets inside quoted strings.
    const firstCurly = trimmed.indexOf('{');
    const firstSquare = trimmed.indexOf('[');
    const first = Math.min(
      firstCurly === -1 ? Infinity : firstCurly,
      firstSquare === -1 ? Infinity : firstSquare
    );

    if (!Number.isFinite(first)) {
      throw new Error('Invalid JSON response');
    }

    const openChar = trimmed[first];
    const closeChar = openChar === '{' ? '}' : ']';

    let depth = 0;
    let inString = false;
    let quoteChar: string | null = null;

    for (let i = first; i < trimmed.length; i++) {
      const ch = trimmed[i];

      if (inString) {
        if (ch === '\\') {
          i++;
          continue;
        }
        if (quoteChar && ch === quoteChar) {
          inString = false;
          quoteChar = null;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = true;
        quoteChar = ch;
        continue;
      }

      if (ch === openChar) depth++;
      if (ch === closeChar) depth--;

      if (depth === 0) {
        const candidate = trimmed.slice(first, i + 1);
        return JSON.parse(candidate);
      }
    }

    throw new Error('Invalid JSON response');
  }
};

const normalizePlayerName = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.replace(/-/g, ' ').trim().toLowerCase();
};

const PlayerProfile: React.FC = () => {
  // Route is `/team/:team/:playerSlug` (legacy `/player/:playerName` URLs redirect here).
  const { playerSlug } = useParams<{ team?: string; playerSlug?: string }>();
  const navigate = useNavigate();
  const playerName = playerSlug ?? '';
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  const [statsByYear, setStatsByYear] = useState<{ year: string, stats: PlayerStatsInterface | null, isCurrent: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

        const playerItems = await fetchItemsWithCache<PlayerInfo>(
          entriesUrl('nomads_players'),
          CACHE_KEYS.players,
          CACHE_TTL_MS
        );

        const normalizedParamName = normalizePlayerName(playerName);
        const playerInfo = (playerItems ?? []).find(
          (p: PlayerInfo) => normalizePlayerName(p.name) === normalizedParamName
        );
        if (!playerInfo) throw new Error('Player not found');

        const isGoalie = playerInfo.position.split('/').some((pos) => pos.trim() === 'Goalie');
        const statsTableName = isGoalie ? 'nomads_goalie_stats' : 'nomads_stats';
        const cacheKey = isGoalie ? CACHE_KEYS.goalieStats : CACHE_KEYS.stats;

        const statsItems = await fetchItemsWithCache<PlayerStatsInterface>(
          entriesUrl(statsTableName),
          cacheKey,
          CACHE_TTL_MS
        );

        const currentStats =
          (statsItems ?? []).find(
            (s) => normalizePlayerName(s.player) === normalizePlayerName(playerName)
          ) ?? null;

        // Try to fetch old stats files - only include years that actually have data
        const oldYears = ['24-25']; // Add more years as needed
        const oldStatsPromises = oldYears.map(async (year) => {
          try {
            const resp = await fetch(`/content/old/${year}-stats.json`);
            if (!resp.ok) {
              return null;
            } // File doesn't exist
            
            const contentType = resp.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              return null; // Not a JSON file
            }
            
            const dataText = await resp.text();
            const data = parseJsonSafely(dataText);
            const oldStatsList: PlayerStatsInterface[] = Array.isArray(data?.Stats)
              ? (data.Stats as PlayerStatsInterface[])
              : Array.isArray(data?.stats)
                ? (data.stats as PlayerStatsInterface[])
                : [];

            const stat =
              oldStatsList.find(
                (s) => normalizePlayerName(s.player) === normalizePlayerName(playerName)
              ) ?? null;
            return stat ? { year, stats: stat } : null;
          } catch (error) {
            console.log(`Error fetching stats for ${year}:`, error);
            return null; // File doesn't exist or invalid JSON
          }
        });
        
        const oldStatsResults = await Promise.all(oldStatsPromises);
        const validOldStats = oldStatsResults.filter((result): result is { year: string; stats: PlayerStatsInterface } => result !== null);

        const allStats = [];
        
        // Add current stats if they exist
        if (currentStats) {
          allStats.push({ year: 'Current', stats: currentStats, isCurrent: true });
        }
        
        // Add old stats
        allStats.push(...validOldStats.map(({ year, stats }) => ({ year, stats, isCurrent: false })));

        setPlayer(playerInfo);
        setStatsByYear(allStats);
        setLoading(false);
      } catch (err) {
        console.error('Error loading player data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [playerName]);

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading player profile...
        </div>
      </ProfileContainer>
    );
  }

  if (error || !player) {
    return (
      <ProfileContainer>
        <BackButton onClick={() => navigate('/player-stats')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Stats
        </BackButton>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          {error || 'Player not found'}
        </div>
      </ProfileContainer>
    );
  }

  const isGoalie = player.position.split('/').some((pos) => pos.trim() === 'Goalie');
  const mainTeamWebsite = getMainTeamWebsite(player.mainTeam, player.mainTeamLink);

  return (
    <ProfileContainer>
      <BackButton onClick={() => navigate('/player-stats')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to Stats
      </BackButton>

      <ProfileCard>
        <ProfileHeader>
          <PlayerImage>
            <img
              src={imageError 
                ? '/images/players/placeholder-player.jpg'
                : `/images/players/${player.name.replace(/\s+/g, '-')}.png`
              }
              alt={player.name}
              onError={() => setImageError(true)}
            />
          </PlayerImage>
          
          <PlayerInfoStyled>
            <h1>
              {player.name}
              <span>#{player.number || 'N/A'}</span>
            </h1>
            <div className="position">{player.position}</div>
          </PlayerInfoStyled>
        </ProfileHeader>

        <ProfileContent>
          <InfoSection>
            <h2>Player Information</h2>
            <InfoGrid>
              <div className="label">Position</div>
              <div className="value">{player.position}</div>
              
              <div className="label">Number</div>
              <div className="value">#{player.number || 'N/A'}</div>

              <div className="label">Shoots</div>
              <div className="value">{player.shoots || 'Right'}</div>

              <div className="label">Main Team</div>
              <div className="value">
                {player.mainTeam
                  ? mainTeamWebsite
                    ? <a href={mainTeamWebsite} target="_blank" rel="noopener noreferrer">{player.mainTeam}</a>
                    : player.mainTeam
                  : 'N/A'}
              </div>
                          
              <div className="label">Fun Fact</div>
              <div className="value">{formatNA(player.funfact)}</div>
              {player.sponsor && (
                <>
                  <div className="label">Sponsor</div>
                  <div className="value">{player.sponsorLink ? <a href={player.sponsorLink}>{player.sponsor}</a> : player.sponsor}</div>
                  <SponsorImage><img src={`/images/sponsors/${player.sponsor.replace(/\s+/g, '-')}.png`} alt={player.sponsor} /></SponsorImage>
                </>
              )}
            </InfoGrid>
          </InfoSection>

          {statsByYear && statsByYear.length > 0 && (
            <InfoSection>
              <h2>Season Statistics</h2>
              <StatsTable>
                <StatHeader>
                  <StatCell>Season</StatCell>
                  <StatCell>GP</StatCell>
                  {isGoalie ? (
                    <>
                      <StatCell>W</StatCell>
                      <StatCell>L</StatCell>
                      <StatCell>SO</StatCell>
                      <StatCell>GA</StatCell>
                      <StatCell>GAA</StatCell>
                      <StatCell>SV%</StatCell>
                    </>
                  ) : (
                    <>
                      <StatCell>G</StatCell>
                      <StatCell>A</StatCell>
                      <StatCell>P</StatCell>
                      <StatCell>PPG</StatCell>
                      <StatCell>PIM</StatCell>
                    </>
                  )}
                </StatHeader>
                {statsByYear.map(({ year, stats, isCurrent }) => (
                  stats ? (
                    <StatRow key={year}>
                      <StatCell>{isCurrent ? config.stats.currentYear : year}</StatCell>
                      <StatCell>{stats.gamesPlayed}</StatCell>
                      {isGoalie ? (
                        <>
                          <StatCell>{Number(stats.wins ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.losses ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.so ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.ga ?? 0) || 0}</StatCell>
                          <StatCell>
                            {(
                              stats.gaa !== undefined
                                ? Number(stats.gaa) || 0
                                : Number(stats.gamesPlayed) > 0
                                  ? (Number(stats.ga ?? 0) || 0) / Number(stats.gamesPlayed)
                                  : 0
                            ).toFixed(2)}
                          </StatCell>
                          <StatCell>
                            {(() => {
                              if (stats.svPercent !== undefined) {
                                const raw = Number(stats.svPercent) || 0;
                                const percent = raw > 1 ? raw : raw * 100;
                                return `${percent.toFixed(2)}%`;
                              }

                              const saves = Number(stats.saves ?? 0) || 0;
                              const shotsAgainst = Number(stats.shotsAgainst ?? 0) || 0;
                              const ratio = saves + shotsAgainst > 0 ? saves / (saves + shotsAgainst) : 0;
                              return `${(ratio * 100).toFixed(2)}%`;
                            })()}
                          </StatCell>
                        </>
                      ) : (
                        <>
                          <StatCell>{Number(stats.goals ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.assists ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.points ?? 0) || 0}</StatCell>
                          <StatCell>{Number(stats.ppg ?? 0).toFixed(2)}</StatCell>
                          <StatCell>{Number(stats.pims ?? 0) || 0}</StatCell>
                        </>
                      )}
                    </StatRow>
                  ) : null
                ))}
              </StatsTable>
            </InfoSection>
          )}
        </ProfileContent>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default PlayerProfile; 